import { Worker, Reservation } from "~/packages/taskrouter";



















export class OutboundTaskHandler {
    readonly #worker: Worker;

    #createdReservations: Reservation[] = [];

    #failedReservations: Reservation[] = [];

    #reservationCreatedHandler: ((reservation: Reservation) => void) | null = null;

    #reservationFailedHandler: ((reservation: Reservation) => void) | null = null;

    constructor(worker: Worker) {
        this.#worker = worker;
    }

    public startListeningReservations() {
        this.#createdReservations = [];
        this.#failedReservations = [];
        this.#reservationCreatedHandler = (reservation: Reservation) => {
            this.#createdReservations.push(reservation);
        };
        this.#reservationFailedHandler = (reservation: Reservation) => {
            this.#failedReservations.push(reservation);
        };
        this.#worker.addListener("reservationCreated", this.#reservationCreatedHandler);
        this.#worker.addListener("reservationFailed", this.#reservationFailedHandler);
    }

    public stopListeningReservations() {
        if (this.#reservationCreatedHandler) {
            this.#worker.removeListener("reservationCreated", this.#reservationCreatedHandler);
            this.#reservationCreatedHandler = null;
        }

        if (this.#reservationFailedHandler) {
            this.#worker.removeListener("reservationFailed", this.#reservationFailedHandler);
            this.#reservationFailedHandler = null;
        }
    }

    public async waitForTaskReservation(taskSid: string, timeoutDuration: number = 10000): Promise<Reservation> {
        return this.waitForReservation((reservation: Reservation) => reservation.task.sid === taskSid, timeoutDuration);
    }

    public async waitForReservation(
        reservationFilter: (Reservation: Reservation) => boolean,
        timeoutDuration: number = 10000
    ): Promise<Reservation> {
        const listeners: {
            innerReservationCreatedHandler: ((reservation: Reservation) => void) | null;
            innerReservationFailedHandler: ((reservation: Reservation) => void) | null;
        } = { innerReservationCreatedHandler: null, innerReservationFailedHandler: null };
        let timeoutId: number | NodeJS.Timeout;

        const removeListeners = () => {
            if (listeners.innerReservationCreatedHandler) {
                this.#worker.removeListener("reservationCreated", listeners.innerReservationCreatedHandler);
            }
            if (listeners.innerReservationFailedHandler) {
                this.#worker.removeListener("reservationFailed", listeners.innerReservationFailedHandler);
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            this.stopListeningReservations();
        };

        return new Promise((resolve, reject) => {
            const createdReservation = this.#createdReservations.find((reservation: Reservation) =>
                reservationFilter(reservation)
            );

            const failedReservation = this.#failedReservations.find((reservation: Reservation) =>
                reservationFilter(reservation)
            );

            if (createdReservation) {
                resolve(createdReservation);
                removeListeners();
            } else if (failedReservation) {
                reject(new Error("Reservation failed"));
                removeListeners();
            } else {
                listeners.innerReservationCreatedHandler = (reservation: Reservation) => {
                    if (reservationFilter(reservation)) {
                        removeListeners();
                        resolve(reservation);
                    }
                };

                listeners.innerReservationFailedHandler = (reservation: Reservation) => {
                    if (reservationFilter(reservation)) {
                        removeListeners();
                        reject(new Error("Reservation failed"));
                    }
                };
                this.#worker.addListener("reservationCreated", listeners.innerReservationCreatedHandler);
                this.#worker.addListener("reservationFailed", listeners.innerReservationFailedHandler);

                timeoutId = setTimeout(() => {
                    reject(new Error("Reservation timeout"));
                    removeListeners();
                }, timeoutDuration);
            }
        });
    }
}
